import React, { useState, useContext, useEffect } from 'react';
import {
  DialogContent,
  Select,
  Textarea,
  SelectOption,
  palettes,
  Theme,
  Input,
  useFlashMessage
} from '@indigo-ag/component-module';
import { faPaperclip } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AgFinancingDocumentType, AgFinancingFile, AgFinancingPhase2Status } from '@indigo-ag/schema';
import { useMutation } from '@apollo/client';
import { ApolloError, PureQueryOptions } from 'apollo-client';
import i18next from 'core/i18n';

import { ModalActionButtons, phase2FileTypes, phase3FileTypes } from 'shared';
import { EnhancedDialog, documentRequestTypesOptions, Permissions, createStylesHook } from 'shared';
import { UserContext } from 'core/components/GateWay/GateWay';
import {
  updateAgFinancingFile,
  getDocumentsQueryByFinancingId,
  financingAgFinancing
} from 'features/brazil-grower/queries';
import { FinanceProcessContext } from 'features/brazil-grower/pages/BrazilGrowersDetail';
import { ModalOptionsMenu } from './ModalOptionsMenu';
import { removeFilesFromAgFinancing } from 'features/brazil-grower/components/BrazilGrowerFinance/utils';

const useStyles = createStylesHook((theme: Theme) => ({
  docSectionTittle: {
    color: '#616161',
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 500,
    margin: '50px 0 21px 5px'
  },
  docIconColor: {
    color: `${palettes.brand.blue.base} !important`,
    fontSize: theme.typography.pxToRem(22),
    marginRight: '22px'
  },
  docNameContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    border: '1px solid #ACC0CA'
  },
  textAreaStyles: {
    margin: '18px 0px 0px 40px !important'
  }
}));

interface EditDocumentModalProps {
  closeModal: () => void;
  file: AgFinancingFile;
  isOpen: boolean;
}

export const EditDocumentModal: React.FunctionComponent<EditDocumentModalProps> = ({ closeModal, file, isOpen }) => {
  const handleDocumentNameEdition = (fileName: string): { fileName: string; fileExtension: string | null } => {
    return {
      fileName: fileName.substring(0, fileName.lastIndexOf('.')) || fileName,
      fileExtension:
        fileName.lastIndexOf('.') >= 0 ? fileName.substring(fileName.lastIndexOf('.'), fileName.length) : null
    };
  };

  const initialDocumentNameState = handleDocumentNameEdition(file?.documentName || '');
  /**
   * State
   */
  const [documentType, setDocumentType] = useState<AgFinancingDocumentType | null>(null);
  const [comment, setComment] = useState(file?.metadata?.comment || '');
  const [commentFieldActive, setCommentFieldActive] = useState(false);
  const [documentName, setDocumentName] = useState(initialDocumentNameState.fileName);
  /**
   * Mutations
   */
  const [updateAgFinancingFileFn] = useMutation(updateAgFinancingFile);

  /**
   * User Context
   */
  const userContext = useContext(UserContext);

  /**
   * Finance Process Context
   */
  const { agFinancingId, agFinance } = useContext(FinanceProcessContext);

  /**
   * Styles
   */
  const classes = useStyles();

  useEffect(() => {
    setDocumentType(file.documentType || null);
    setComment(file.metadata?.comment || '');
    setCommentFieldActive(false);
    setDocumentName(initialDocumentNameState.fileName || '');
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateDocumentType = (e: string | null) => {
    setDocumentType(e as AgFinancingDocumentType);
  };

  const updateDocumentName = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDocumentName(e.target.value);
  };

  const handleComment = (e: React.ChangeEvent<HTMLTextAreaElement>) => setComment(e.target.value);

  const toggleCommentField = () => setCommentFieldActive(!commentFieldActive);
  const removeFile = () => {
    setDocumentName('');
    setComment('');
  };

  const commonRefetchQueries: PureQueryOptions[] = [
    {
      ...getDocumentsQueryByFinancingId(agFinancingId)
    }
  ];

  const { addFlashMessage } = useFlashMessage();

  const updateDocumentInfo = (refetchQueries: PureQueryOptions[]) => {
    const editedDocumentName = `${documentName}${initialDocumentNameState.fileExtension || ''}`;
    const updatedDocument = [
      {
        id: file.id,
        documentType,
        documentName: editedDocumentName,
        metadata: {
          ...(file.metadata || {}),
          comment
        }
      }
    ];
    updateAgFinancingFileFn({
      variables: {
        data: updatedDocument
      },
      refetchQueries
    }).catch((e: ApolloError) => {
      addFlashMessage({
        content: e.graphQLErrors[0].message,
        title: '',
        variant: 'error'
      });
    });
  };

  const updateDocument = () =>
    new Promise<void>((resolve, reject) => {
      if (documentType && documentName) {
        const refetchQueries: PureQueryOptions[] = [...commonRefetchQueries];

        // * refetch financing process info to display phase3.files in the execution tab
        if (documentType !== file.documentType && phase3FileTypes.includes(documentType)) {
          refetchQueries.push({ query: financingAgFinancing, variables: { agFinancingId } });
        }

        updateDocumentInfo(refetchQueries);
        resolve();
      }
      if (documentName.length === 0) {
        removeFilesFromAgFinancing([file?.id as AgFinancingFile['id']], commonRefetchQueries).catch(
          (e: ApolloError) => {
            addFlashMessage({
              content: e.graphQLErrors[0]?.message || i18next.t('error'),
              title: '',
              variant: 'error'
            });
          }
        );
        resolve();
      } else {
        reject();
      }
    });

  const renderOptions = (options: SelectOption[]) => {
    const isLuchesi = userContext.hasPermission(Permissions.FA_EXTERNAL_LEGAL);
    const isFlc = userContext.hasPermission(Permissions.FA_EXTERNAL_CREDIT);

    if (isLuchesi) {
      let filteredOptions = options;

      // Luchesi on Phase 2
      if (agFinance?.phase2?.status !== AgFinancingPhase2Status.APPROVED) {
        if (
          [...phase2FileTypes, AgFinancingDocumentType.SALESFORCE_DOCUMENT].includes(
            file?.documentType as AgFinancingDocumentType
          )
        ) {
          filteredOptions = filteredOptions.filter(option =>
            phase2FileTypes.includes(option.value as AgFinancingDocumentType)
          );
        }
      } else {
        // Luchesi on Phase 3
        if (
          [
            ...phase3FileTypes,
            AgFinancingDocumentType.SALESFORCE_DOCUMENT,
            AgFinancingDocumentType.REGISTERED_CONTRACT
          ].includes(file?.documentType as AgFinancingDocumentType)
        ) {
          filteredOptions = filteredOptions.filter(option =>
            [...phase3FileTypes, AgFinancingDocumentType.REGISTERED_CONTRACT].includes(
              option.value as AgFinancingDocumentType
            )
          );
        }
      }

      return filteredOptions;
    }
    if (isFlc) {
      return options.filter(
        option =>
          option.value !== AgFinancingDocumentType.MATRICULA &&
          option.value !== AgFinancingDocumentType.CONSENT_LETTER &&
          option.value !== AgFinancingDocumentType.PROMISSORY_NOTE &&
          option.value !== AgFinancingDocumentType.CPR
      );
    }

    return options;
  };
  const canEditDocumentType = file?.documentType !== AgFinancingDocumentType.MATRICULA;

  return (
    <EnhancedDialog
      open={isOpen}
      onClose={closeModal}
      title={`${i18next.t('edit')} ${i18next.t('document')}`}
      dialogMinHeight="280px"
    >
      <DialogContent>
        {canEditDocumentType && (
          <Select
            label={i18next.t('documentType')}
            name="documentType"
            options={renderOptions(documentRequestTypesOptions)}
            value={documentType}
            onSelectionChanged={updateDocumentType}
            placeholder={i18next.t('pleaseSelect')}
          />
        )}
        <Input label={`${i18next.t('documentName')}`} value={documentName} onChange={updateDocumentName} />
        <div className={classes.docSectionTittle}>{i18next.t('document')}</div>
        {documentName && (
          <div className={classes.docNameContainer}>
            <div>
              <FontAwesomeIcon icon={faPaperclip} size="lg" className={classes.docIconColor} />
              {file?.documentName}
            </div>
            <ModalOptionsMenu
              addCommentAction={toggleCommentField}
              isDisabled={comment.length > 0}
              removeFileAction={canEditDocumentType ? removeFile : undefined}
            />
          </div>
        )}
        {commentFieldActive || (comment.length > 0 && documentName) ? (
          <Textarea
            className={classes.textAreaStyles}
            label={i18next.t('comment')}
            onChange={handleComment}
            resizable
            value={comment}
          />
        ) : null}
      </DialogContent>
      <ModalActionButtons
        changeButtonName={`${i18next.t('save')} ${i18next.t('changes')}`}
        closeModal={closeModal}
        disabled={!documentType && (!!file?.documentName || !documentName)}
        save={updateDocument}
      />
    </EnhancedDialog>
  );
};
